import { takeCheckoutReturnOrg } from '@/lib/checkout-return'
import { createFileRoute, redirect } from '@tanstack/react-router'

/**
 * Landing route for the return from Stripe-hosted Checkout (task 5.7).
 *
 * Lago's Stripe `success_redirect_url` is a single, org-agnostic URL per
 * environment (it carries no org/session identifier — see
 * {@link takeCheckoutReturnOrg}), so it points here. We recover the org the
 * admin was managing — stashed in `sessionStorage` just before the redirect —
 * and forward to that org's billing page with the `?checkout=success` marker
 * the billing route already handles (return banner, billing refetch, and the
 * Starter → Pro auto-continue). The redirect happens in `beforeLoad` so the
 * org-agnostic URL never renders — the admin only ever sees their billing page.
 *
 * Fallback: if the org can't be recovered (cold load, or a new tab/session that
 * lost `sessionStorage`), send them to the org list rather than a dead page.
 *
 * Note: Lago's Checkout Session sets only a `success_url`, no `cancel_url`, so
 * this route is reached only on a completed card setup — there is no "cancelled"
 * variant to handle here.
 */
export const Route = createFileRoute('/_authenticated/billing/checkout-return')(
  {
    beforeLoad: () => {
      const orgId = takeCheckoutReturnOrg()
      if (orgId) {
        throw redirect({
          to: '/orgs/$orgId/billing',
          params: { orgId },
          search: { checkout: 'success' },
          replace: true,
        })
      }
      throw redirect({ to: '/orgs', replace: true })
    },
    // beforeLoad always redirects, so this never renders — present only to
    // satisfy the route definition.
    component: () => null,
  },
)
